import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Instagram, Youtube, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { Container, SectionHeader } from "@/components/Section";
import { useSite } from "@/context/SiteContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { waLink } from "@/lib/api";

export default function Contact() {
  const { site } = useSite();
  const { openEnquiry } = useEnquiry();
  const wa = waLink(site.whatsapp, "Hello PTG, I would like to know more about admissions.");

  const rows = [
    { icon: MapPin, label: "Address", value: site.address },
    { icon: Phone, label: "Phone", value: site.phone },
    { icon: Mail, label: "Email", value: site.email },
    { icon: Clock, label: "Office / Teaching Hours", value: site.hours },
  ];

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us" description="Contact Prasad Tutor Group (PTG), Khar East, Mumbai. Find our address, phone, WhatsApp, email and office hours, or submit an admission enquiry." />
      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <SectionHeader center={false} light eyebrow="Contact Us" title="We'd love to hear from you"
            subtitle="Reach out for admissions, batch timings or any questions about PTG." />
        </Container>
      </section>

      <section className="py-16 sm:py-24">
        <Container className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="space-y-5">
              {rows.map((r) => (
                <div key={r.label} className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid={`contact-${r.label.toLowerCase().split(" ")[0]}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-blue-900 shrink-0"><r.icon className="h-5 w-5" /></div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{r.label}</p>
                    <p className="text-slate-700 mt-0.5">{r.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href={wa} target="_blank" rel="noreferrer"><Button className="bg-emerald-500 hover:bg-emerald-600 h-11" data-testid="contact-whatsapp-btn"><MessageCircle className="mr-2 h-5 w-5" /> WhatsApp Us</Button></a>
              <Button onClick={() => openEnquiry()} className="bg-amber-500 hover:bg-amber-600 font-bold h-11" data-testid="contact-enquiry-btn">Admission Enquiry</Button>
              {site.instagram_url && <a href={site.instagram_url} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" className="h-11 w-11" data-testid="contact-instagram"><Instagram className="h-5 w-5" /></Button></a>}
              {site.youtube_url && <a href={site.youtube_url} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" className="h-11 w-11" data-testid="contact-youtube"><Youtube className="h-5 w-5" /></Button></a>}
            </div>
            {(site.maps_link || site.maps_embed) && (
              <a href={site.maps_link || site.maps_embed} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-900 hover:text-blue-950 underline underline-offset-2" data-testid="contact-google-review">
                <ExternalLink className="h-4 w-4" /> Review us on Google
              </a>
            )}
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[360px] relative" data-testid="contact-map">
            {site.maps_embed ? (
              <>
                <iframe title="Prasad Tutor Group location on Google Maps" src={site.maps_embed} className="w-full h-full min-h-[360px] block" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                <a href={site.maps_link || site.maps_embed} target="_blank" rel="noreferrer"
                  className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-blue-900 text-white px-4 py-2 text-sm font-semibold shadow-lg hover:bg-blue-950 transition-colors"
                  data-testid="contact-open-maps">
                  <ExternalLink className="h-4 w-4" /> Open in Google Maps
                </a>
              </>
            ) : (
              <div className="h-full min-h-[360px] flex flex-col items-center justify-center bg-slate-50 text-slate-400 gap-3 p-6 text-center">
                <MapPin className="h-12 w-12" />
                <p className="font-medium">Google Maps location will appear here</p>
                <p className="text-sm">Our location map will be shown here.</p>
              </div>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
